import { api } from './api';

/**
 * ✅ ALWAYS send both from & to
 * Prevents backend fallback to range (month/week)
 */
const buildParams = (filters = {}) => ({
  from: filters.from ?? null,
  to: filters.to ?? null
});

// Safe wrapper to handle errors and empty responses
const safeGet = async (request, defaultValue) => {
  try {
    const res = await request();
    return res.data ?? defaultValue;
  } catch (err) {
    // ✅ Ignore cancelled requests silently
    if (
      err.name === 'CanceledError' ||
      err.code === 'ERR_CANCELED' ||
      err.message === 'canceled'
    ) {
      return defaultValue;
    }

    console.error('Analytics API error:', err.message);
    return defaultValue;
  }
};

/* ---------------- SUMMARY ---------------- */

export const getSummary = (filters, signal) =>
  safeGet(
    () =>
      api.get('/analytics/summary', {
        params: buildParams(filters),
        signal
      }),
    {
      revenue: 0,
      appointments: 0,
      clients: 0
    }
  );

/* ---------------- REVENUE ---------------- */

export const getRevenueTrend = (params, signal) =>
  safeGet(
    () =>
      api.get('/analytics/revenue/trend', {
        params,
        signal
      }),
    { bucket: 'day', data: [] }
  );

export const getRevenueByCategory = (params, signal) =>
  safeGet(
    () =>
      api.get('/analytics/revenue/by-category', {
        params,
        signal
      }),
    []
  );

export const getRevenueByPaymentMethod = (params, signal) =>
  safeGet(
    () =>
      api.get('/analytics/revenue/payment-split', {
        params,
        signal
      }),
    []
  );

/* ---------------- APPOINTMENTS ---------------- */

export const getAppointmentsOverview = (params, signal) =>
  safeGet(
    () =>
      api.get('/analytics/appointments/overview', {
        params,
        signal
      }),
    { bucket: 'day', data: [] }
  );

export const getAppointmentsPeakHours = (params, signal) =>
  safeGet(
    () =>
      api.get('/analytics/appointments/peak-hours', {
        params,
        signal
      }),
    []
  );

/* ---------------- STAFF ---------------- */

export const getStaffRevenue = (filters, signal) =>
  safeGet(
    () =>
      api.get('/analytics/staff/revenue', {
        params: buildParams(filters),
        signal
      }),
    []
  );

export const getStaffUtilization = (filters, signal) =>
  safeGet(
    () =>
      api.get('/analytics/staff/utilization', {
        params: buildParams(filters),
        signal
      }),
    []
  );

/* ---------------- CLIENTS ---------------- */

export const getNewVsReturningClients = (filters, signal) =>
  safeGet(
    () =>
      api.get('/analytics/clients/new-vs-returning', {
        params: buildParams(filters),
        signal
      }),
    []
  );

export const getClientRetentionTrend = (filters, signal) =>
  safeGet(
    () =>
      api.get('/analytics/clients/retention-trend', {
        params: buildParams(filters),
        signal
      }),
    { bucket: 'day', data: [] }
  );

export const getTopSpendingClients = (filters, signal) =>
  safeGet(
    () =>
      api.get('/analytics/clients/top-spenders', {
        params: buildParams(filters),
        signal
      }),
    []
  );

/* ---------------- INVENTORY ---------------- */

export const getLowStock = (signal) =>
  safeGet(
    () =>
      api.get('/analytics/inventory/low-stock', {
        signal
      }),
    []
  );

export const getProductSales = (filters, signal) =>
  safeGet(
    () =>
      api.get('/analytics/inventory/sales', {
        params: buildParams(filters),
        signal
      }),
    []
  );